import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { CasaLeonWorldSnapshot } from '@casa-leon-vivo/contracts/casa-leon';
import { Soundscape } from './audio/soundscape.js';
import { tickCharacters, type CharacterAgent } from './entities/characters.js';
import { spawnStylizedCast } from './entities/stylized-staff.js';
import { PremiumPilotController } from '../pilot/premium-pilot-controller.js';
import type { CasaLeon3DLayoutEditorHandlers } from './types.js';
import type { FloorLayoutModule } from '../floor/dynamic-floor.js';
import { advanceSimPanels, initialSimPanels } from './simulation/demo-panels.js';
import type {
  CasaLeon3DCallbacks,
  CasaLeon3DExperience,
  CasaLeon3DInteractiveId,
  CasaLeon3DPerfStats,
  CasaLeon3DZone,
  CasaLeon3DSimPanels,
} from './types.js';
import { INTERACTIVE_USER_DATA_KEY } from './types.js';
import type { WeatherVisualState } from '../weather/weather-types.js';
import { AmbientEffects } from './world/ambient-effects.js';
import { buildRestaurantWorld } from './world/build-restaurant.js';
import { WeatherEffects } from './world/weather-effects.js';
import { applyCameraPreset, type CameraPresetId } from './camera-presets.js';
import { GuestVisualLayer } from './entities/guest-visual-layer.js';
import { DynamicTableLayer } from './world/dynamic-table-layer.js';
import { setupRestaurantLighting } from './world/lighting.js';
import { PALETTE } from './world/palette.js';
import { applyRendererPerfProfile } from './perf-profile.js';
import { DecorEditorLayer } from './world/decor-editor-layer.js';
import type { DecorPlacement, TableLayoutOffset } from '../editor/layout-types.js';
import type { VivoAppMode } from './types.js';

const TABLE_STATE_COLORS: Record<string, number> = {
  abierta: 0x9c6b3c,
  pedido: 0xe8a838,
  servicio: 0x48bb78,
  cobro: 0xed8936,
  libre: 0x6b7280,
};

function zoneForInteractive(id: CasaLeon3DInteractiveId): CasaLeon3DZone {
  if (id.startsWith('table-')) return 'salon';
  switch (id) {
    case 'kitchen-board':
      return 'cocina';
    case 'whatsapp-sign':
      return 'entrada';
    case 'phone':
    case 'register':
      return 'caja';
    case 'weather-station':
      return 'entrada';
    default:
      return 'entrada';
  }
}

function labelForInteractive(id: CasaLeon3DInteractiveId): string {
  if (id.startsWith('table-')) return `Mesa ${id.replace('table-', '')}`;
  const map: Record<string, string> = {
    'kitchen-board': 'Pizarr�n de cocina',
    'whatsapp-sign': 'Cartel WhatsApp',
    phone: 'Tel�fono',
    register: 'Caja',
    'weather-station': 'Estaci�n meteorol�gica',
  };
  return map[id] ?? id;
}

function findInteractiveRoot(obj: THREE.Object3D | null): THREE.Object3D | null {
  let cur: THREE.Object3D | null = obj;
  while (cur) {
    if (cur.userData[INTERACTIVE_USER_DATA_KEY]) return cur;
    cur = cur.parent;
  }
  return null;
}

export function createCasaLeon3DExperience(
  container: HTMLElement,
  callbacks: CasaLeon3DCallbacks = {},
): CasaLeon3DExperience {
  const scene = new THREE.Scene();
  const mobileView = window.innerWidth < 900;
  const captureMode = import.meta.env.VITE_VIVO_CAPTURE === '1';
  const lighting = setupRestaurantLighting(scene, mobileView);

  const camera = new THREE.PerspectiveCamera(42, 1, 0.5, 140);
  const defaultCameraPos = new THREE.Vector3(28, 26, 28);
  const defaultTarget = new THREE.Vector3(0, 0.15, 0);
  camera.position.copy(defaultCameraPos);

  const renderer = new THREE.WebGLRenderer({
    antialias: !mobileView,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: captureMode || true,
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.22;
  applyRendererPerfProfile(renderer, { mobile: mobileView, captureMode });
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.06;
  controls.maxPolarAngle = Math.PI / 2.35;
  controls.minPolarAngle = 0.45;
  controls.minDistance = 8;
  controls.maxDistance = 52;
  controls.target.copy(defaultTarget);

  const built = buildRestaurantWorld();
  scene.add(built.root);
  const tableLayer = new DynamicTableLayer();
  scene.add(tableLayer.root);
  const decorLayer = new DecorEditorLayer();
  scene.add(decorLayer.root);
  const guestLayer = new GuestVisualLayer();
  scene.add(guestLayer.root);
  let appMode: VivoAppMode = 'simulation';
  let selectedModuleId: string | null = null;
  let editorOffsets: TableLayoutOffset[] = [];
  let editorDecor: DecorPlacement[] = [];
  const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const dragHit = new THREE.Vector3();
  let draggingModule: string | null = null;
  let dragStartWorld = new THREE.Vector3();
  let dragBaseOffset = { dx: 0, dz: 0 };
  const ambientFx = new AmbientEffects(built.root, built.grillLightPos);
  const weatherFx = new WeatherEffects(built.root, new THREE.Vector3(10, 0, 2));
  for (const bush of built.foliage) weatherFx.registerFoliage(bush);

  const logoLoader = new THREE.TextureLoader();
  logoLoader.load(
    `${import.meta.env.BASE_URL}brand/logo.png`,
    (tex) => built.brandSign.applyLogo(tex),
    undefined,
    () => {
      /* logo opcional en preview sin asset */
    },
  );

  const agents: CharacterAgent[] = spawnStylizedCast(built.root);
  let premiumPilot: PremiumPilotController | null = null;
  void PremiumPilotController.mount(built.root).then((p) => {
    premiumPilot = p;
    callbacks.onCharactersReady?.();
  });
  let layoutEditorHandlers: CasaLeon3DLayoutEditorHandlers = {};

  const pickInteractives = () => [...built.interactives, ...tableLayer.getInteractives()];

  const soundscape = new Soundscape();
  let simPanels: CasaLeon3DSimPanels = initialSimPanels();
  callbacks.onSimPanelsUpdate?.(simPanels);

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let focused: THREE.Object3D | null = null;
  let ringActive = false;

  const highlightMat = new THREE.MeshStandardMaterial({
    color: PALETTE.accent,
    emissive: PALETTE.accent,
    emissiveIntensity: 0.5,
    transparent: true,
    opacity: 0.28,
    depthWrite: false,
  });
  const highlight = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), highlightMat);
  highlight.visible = false;
  scene.add(highlight);

  const keys = new Set<string>();
  const onKeyDown = (e: KeyboardEvent) => keys.add(e.key.toLowerCase());
  const onKeyUp = (e: KeyboardEvent) => keys.delete(e.key.toLowerCase());
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  const resize = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  };
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(container);

  const emitFocus = (id: CasaLeon3DInteractiveId) => {
    callbacks.onInteractiveFocus?.({
      id,
      zone: zoneForInteractive(id),
      label: labelForInteractive(id),
    });
    soundscape.playClick();
  };

  const updatePointer = (ev: PointerEvent) => {
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
  };

  const onPointerMove = (ev: PointerEvent) => {
    updatePointer(ev);
    raycaster.setFromCamera(pointer, camera);
    if (appMode === 'edit' && draggingModule) {
      if (raycaster.ray.intersectPlane(dragPlane, dragHit)) {
        const snap = layoutEditorHandlers.snapModule?.(draggingModule, dragHit.x, dragHit.z);
        if (snap) {
          tableLayer.setDragPreview(draggingModule, snap.worldX, snap.worldZ);
        } else {
          const dx = dragHit.x - dragStartWorld.x;
          const dz = dragHit.z - dragStartWorld.z;
          const off = editorOffsets.find((o) => o.moduleId === draggingModule);
          if (off) {
            off.dx = dragBaseOffset.dx + dx;
            off.dz = dragBaseOffset.dz + dz;
            tableLayer.applyEditorOffsets(editorOffsets);
          }
        }
      }
      return;
    }
    const hits = raycaster.intersectObjects(pickInteractives(), true);
    const hit = findInteractiveRoot(hits[0]?.object ?? null);
    const mod = tableLayer.pickModuleId(hits[0]?.object ?? null);
    if (appMode === 'edit') {
      renderer.domElement.style.cursor = mod ? 'grab' : 'default';
      return;
    }
    if (hit !== focused) {
      focused = hit;
      renderer.domElement.style.cursor = hit ? 'pointer' : 'default';
    }
  };

  const onPointerDown = (ev: PointerEvent) => {
    if (appMode !== 'edit') return;
    updatePointer(ev);
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(tableLayer.getGroups(), true);
    const mod = tableLayer.pickModuleId(hits[0]?.object ?? null);
    if (!mod) return;
    draggingModule = mod;
    selectedModuleId = mod;
    controls.enabled = false;
    const off = editorOffsets.find((o) => o.moduleId === mod) ?? { moduleId: mod, logicalTableId: 0, dx: 0, dz: 0, rotationY: 0 };
    dragBaseOffset = { dx: off.dx, dz: off.dz };
    if (raycaster.ray.intersectPlane(dragPlane, dragHit)) dragStartWorld.copy(dragHit);
  };

  const onPointerUp = () => {
    if (appMode === 'edit' && draggingModule && raycaster.ray.intersectPlane(dragPlane, dragHit)) {
      layoutEditorHandlers.onModuleDrop?.(draggingModule, dragHit.x, dragHit.z);
      tableLayer.setDragPreview(draggingModule, null, null);
    }
    draggingModule = null;
    controls.enabled = true;
  };

  const onClick = () => {
    if (appMode === 'edit') return;
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(pickInteractives(), true);
    const hit = findInteractiveRoot(hits[0]?.object ?? null);
    if (!hit) return;
    const id = hit.userData[INTERACTIVE_USER_DATA_KEY] as CasaLeon3DInteractiveId;
    emitFocus(id);
    const box = new THREE.Box3().setFromObject(hit);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    highlight.scale.copy(size.clone().multiplyScalar(1.12));
    highlight.position.copy(center);
    highlight.visible = true;
    controls.target.copy(center);
  };

  renderer.domElement.addEventListener('pointermove', onPointerMove);
  renderer.domElement.addEventListener('pointerdown', onPointerDown);
  renderer.domElement.addEventListener('pointerup', onPointerUp);
  renderer.domElement.addEventListener('click', onClick);

  let lastSim = 0;
  let animId = 0;
  const clock = new THREE.Clock();
  let perfAccum = 0;
  let perfFrames = 0;
  let lastPerf: CasaLeon3DPerfStats | null = null;

  const animate = () => {
    animId = requestAnimationFrame(animate);
    const dt = Math.min(clock.getDelta(), 0.05);
    const t = clock.elapsedTime;

    const pan = 12 * dt;
    if (keys.has('w')) controls.target.z -= pan;
    if (keys.has('s')) controls.target.z += pan;
    if (keys.has('a')) controls.target.x -= pan;
    if (keys.has('d')) controls.target.x += pan;

    lighting.update(t);
    ambientFx.tick(dt, t);
    weatherFx.tick(dt, t);
    tableLayer.tick(dt);
    if (appMode === 'simulation') {
      tickCharacters(agents, dt, t);
      premiumPilot?.update(dt);
      callbacks.onFrame?.(dt);
    }
    controls.update();

    if (t - lastSim > 1.2) {
      lastSim = t;
      simPanels = advanceSimPanels(simPanels);
      callbacks.onSimPanelsUpdate?.(simPanels);
      if (simPanels.phone.state === 'ringing' && !ringActive) {
        soundscape.playPhoneRing();
        ringActive = true;
      }
      if (simPanels.phone.state !== 'ringing') {
        soundscape.stopRing();
        ringActive = false;
      }
    }

    renderer.render(scene, camera);

    perfFrames += 1;
    perfAccum += dt;
    if (perfAccum >= 1) {
      lastPerf = {
        fps: Math.round(perfFrames / perfAccum),
        frameTimeMs: Math.round((perfAccum / perfFrames) * 1000),
        drawCalls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
        dpr: renderer.getPixelRatio(),
        preserveDrawingBuffer: Boolean(renderer.getContextAttributes()?.preserveDrawingBuffer),
      };
      callbacks.onPerfUpdate?.(lastPerf);
      perfFrames = 0;
      perfAccum = 0;
    }
  };
  animate();

  const applySnapshot = (snapshot: CasaLeonWorldSnapshot) => {
    for (const table of snapshot.tables) {
      const mesh = tableLayer.tableTops.get(table.tableId) ?? built.tableMeshes.get(table.tableId);
      if (!mesh) continue;
      const m = mesh.material as THREE.MeshStandardMaterial;
      const c = TABLE_STATE_COLORS[table.operationalState] ?? TABLE_STATE_COLORS.abierta;
      m.color.setHex(c);
      m.emissive.setHex(table.operationalState === 'cobro' ? 0x553300 : 0x000000);
      m.emissiveIntensity = table.operationalState === 'cobro' ? 0.25 : 0;
    }
    if (snapshot.register) {
      simPanels = {
        ...simPanels,
        register: {
          ...simPanels.register,
          open: snapshot.register.open,
          salesToday: snapshot.register.salesToday,
        },
      };
      callbacks.onSimPanelsUpdate?.(simPanels);
    }
  };

  return {
    setSnapshot(snapshot) {
      applySnapshot(snapshot);
    },
    setFloorLayout(layout: FloorLayoutModule[]) {
      tableLayer.syncLayout(layout);
      tableLayer.applyEditorOffsets(editorOffsets);
      premiumPilot?.setLayout(layout);
    },
    setAppMode(mode: VivoAppMode) {
      appMode = mode;
      highlight.visible = false;
      controls.enabled = true;
      draggingModule = null;
    },
    setLayoutEditorState(offsets, decor) {
      editorOffsets = offsets;
      editorDecor = decor;
      tableLayer.applyEditorOffsets(editorOffsets);
      decorLayer.sync(editorDecor);
    },
    setLayoutEditorHandlers(handlers) {
      layoutEditorHandlers = handlers;
    },
    restartPremiumPilot() {
      premiumPilot?.restart();
    },
    setPremiumPilotLayout(layout) {
      premiumPilot?.setLayout(layout);
    },
    getSelectedLayoutModuleId() {
      return selectedModuleId;
    },
    setWeatherVisual(state: WeatherVisualState) {
      lighting.applySkyMood(state.mood);
      weatherFx.setVisualState(state);
    },
    setGuestMarkers(markers) {
      guestLayer.sync(markers);
    },
    setCameraPreset(preset: CameraPresetId) {
      applyCameraPreset(camera, controls, preset);
    },
    getPerf() {
      return lastPerf;
    },
    setSoundscapeEnabled(enabled) {
      soundscape.setEnabled(enabled);
    },
    async unlockAudio() {
      await soundscape.unlock();
      soundscape.setEnabled(true);
      soundscape.startAmbient();
    },
    resetCamera() {
      applyCameraPreset(camera, controls, 'overview');
      highlight.visible = false;
    },
    sampleCanvasBrightness() {
      const gl = renderer.getContext();
      const w = renderer.domElement.width;
      const h = renderer.domElement.height;
      if (!w || !h) return 0;
      const pixels = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
      let sum = 0;
      let n = 0;
      const cx = Math.floor(w / 2);
      const cy = Math.floor(h / 2);
      const r = Math.min(80, Math.floor(w * 0.12));
      for (let y = cy - r; y < cy + r; y += 4) {
        for (let x = cx - r; x < cx + r; x += 4) {
          if (x < 0 || y < 0 || x >= w || y >= h) continue;
          const i = (y * w + x) * 4;
          sum += pixels[i] + pixels[i + 1] + pixels[i + 2];
          n += 1;
        }
      }
      return n ? sum / (n * 3) : 0;
    },
    focusInteractive(id) {
      const obj = pickInteractives().find((o) => {
        const root = findInteractiveRoot(o);
        return root?.userData[INTERACTIVE_USER_DATA_KEY] === id;
      });
      if (!obj) {
        const tableMesh = tableLayer.tableTops.get(id.replace('table-', ''));
        if (tableMesh) {
          controls.target.copy(tableMesh.position);
          emitFocus(id);
        }
        return;
      }
      const box = new THREE.Box3().setFromObject(obj);
      controls.target.copy(box.getCenter(new THREE.Vector3()));
      emitFocus(id);
    },
    resize,
    destroy() {
      cancelAnimationFrame(animId);
      ro.disconnect();
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      renderer.domElement.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      ambientFx.dispose();
      weatherFx.dispose();
      soundscape.destroy();
      controls.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
    },
  };
}
